import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, XCircle, Clock, Zap, Trophy,
  ChevronRight, ChevronLeft, Send, RotateCcw, AlertCircle,
  BarChart2, ClipboardList, Shield
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

/* ── Fisher-Yates shuffle ──────────────────────────────────────── */
const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const MicroAssessmentPlayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [assessment, setAssessment] = useState(null);
  const [questions, setQuestions] = useState([]);   // possibly shuffled
  const [answers, setAnswers] = useState({});        // { questionId: 'A' | 'B' | 'C' | 'D' }
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [phase, setPhase] = useState('start');       // start | taking | result
  const [result, setResult] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);  // for paginated mode
  const [startTime, setStartTime] = useState(null);

  /* ── Fetch assessment ── */
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/assessments/${id}`);
        if (!data.success) throw new Error();
        const a = data.data;
        setAssessment(a);

        // Check if already submitted
        const myAttempts = (a.responses || []).filter(
          (r) => r.user?.toString() === user?._id?.toString() || r.user === user?._id
        );
        const maxAttempts = a.maxAttempts || 1;
        const canRetake = myAttempts.length > 0 && myAttempts.length < maxAttempts;

        if (myAttempts.length > 0 && !canRetake) {
          const myAttempt = myAttempts[myAttempts.length - 1];
          const totalQs = a.questions?.length || 0;
          const correctCount = (myAttempt.answers || []).filter((ans) => ans.isCorrect).length;
          setResult({
            score: myAttempt.score ?? correctCount,
            total: myAttempt.totalScore || totalQs,
            percentage: myAttempt.percentage || (totalQs > 0 ? Math.round((correctCount / totalQs) * 100) : 0),
            timeTaken: myAttempt.timeTaken || 0,
            alreadySubmitted: true,
            attemptNumber: myAttempt.attemptNumber,
            breakdown: a.latestAttempt?.breakdown,
          });
          setPhase('result');
          setLoading(false);
          return;
        }

        setAssessment({ ...a, _priorAttempts: myAttempts.length });
        const qs = a.randomizeQuestions ? shuffle(a.questions || []) : (a.questions || []);
        setQuestions(qs);
      } catch {
        toast.error('Could not load assessment');
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, user]);

  /* ── Handlers ── */
  const handleStart = () => {
    setPhase('taking');
    setStartTime(Date.now());
    setCurrentIdx(0);
  };

  const handleAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length < questions.length) {
      toast.error('Please answer all questions before submitting');
      return;
    }
    setSubmitting(true);
    try {
      const timeTaken = startTime ? Math.round((Date.now() - startTime) / 1000) : 0;
      const payload = {
        answers: questions.map((q) => ({
          questionId: q._id,
          selectedValue: answers[q._id],
        })),
        timeTaken,
      };
      const { data } = await api.post(`/assessments/${id}/submit`, payload);
      if (!data.success) throw new Error(data.message);

      setResult({
        score: data.data.score,
        total: data.data.total,
        percentage: data.data.percentage,
        timeTaken: data.data.timeTaken ?? timeTaken,
        attemptNumber: data.data.attemptNumber,
        breakdown: data.data.breakdown || [],
      });
      setPhase('result');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const answeredCount = Object.keys(answers).length;
  const progress = questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0;

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1a3884] to-[#00152E] flex items-center justify-center mx-auto mb-4 animate-pulse shadow-xl">
            <ClipboardList className="w-8 h-8 text-white" />
          </div>
          <p className="text-gray-500 font-medium">Loading assessment…</p>
        </div>
      </div>
    );
  }

  /* ── START SCREEN ── */
  if (phase === 'start') {
    return (
      <div className="max-w-2xl mx-auto pt-8 pb-16 px-4">
        <button
          onClick={() => navigate('/student/micro-assessments')}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#1a3884] mb-8 transition group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to Assessments
        </button>

        <div className="bg-white rounded-3xl border border-gray-100 shadow-2xl overflow-hidden">
          {/* Hero */}
          <div className="bg-gradient-to-br from-[#00152E] via-[#001a3d] to-[#1a3884] p-10 text-white text-center relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-white/5 blur-2xl" />
            <div className="absolute bottom-0 -left-6 w-24 h-24 rounded-full bg-blue-400/10 blur-2xl" />
            <div className="relative">
              <div className="w-20 h-20 bg-white/15 backdrop-blur-sm rounded-3xl flex items-center justify-center mx-auto mb-5 border border-white/10 shadow-xl">
                <ClipboardList className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-2xl font-black mb-2">{assessment?.assessmentName}</h1>
              {assessment?.description && (
                <p className="text-blue-200 text-sm leading-relaxed max-w-md mx-auto">
                  {assessment.description}
                </p>
              )}
            </div>
          </div>

          <div className="p-8 space-y-7">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: 'Questions', value: questions.length, icon: BarChart2, color: 'text-[#1a3884] bg-blue-50 border-blue-100' },
                { label: 'Randomized', value: assessment?.randomizeQuestions ? 'Yes' : 'No', icon: Zap, color: 'text-violet-600 bg-violet-50 border-violet-100' },
                { label: 'Attempts', value: `${assessment?.responses?.length || 0}/${assessment?.maxAttempts || 1}`, icon: RotateCcw, color: 'text-amber-600 bg-amber-50 border-amber-100' },
              ].map(({ label, value, icon: Icon, color }) => (
                <div key={label} className={`flex flex-col items-center justify-center p-4 rounded-2xl border ${color}`}>
                  <Icon className="w-5 h-5 mb-1.5 opacity-70" />
                  <p className="text-lg font-black">{value}</p>
                  <p className="text-[10px] uppercase tracking-wider font-semibold opacity-60 mt-0.5">{label}</p>
                </div>
              ))}
            </div>

            {/* Instructions */}
            {assessment?.instructions && (
              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">Stream / Category</span>
                </div>
                <p className="text-sm text-amber-800 font-medium">{assessment.instructions}</p>
              </div>
            )}

            {/* Rules */}
            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100 space-y-2.5">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Guidelines</p>
              {[
                'Answer all questions to complete the assessment',
                'Each correct MCQ answer earns 1 point',
                'You can review any question before submitting',
                assessment?.randomizeQuestions && 'Questions appear in randomized order for fairness',
              ].filter(Boolean).map((rule, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-[#1a3884]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Shield className="w-2.5 h-2.5 text-[#1a3884]" />
                  </div>
                  <p className="text-sm text-slate-600">{rule}</p>
                </div>
              ))}
            </div>

            <button
              onClick={handleStart}
              className="w-full py-4 bg-gradient-to-r from-[#1a3884] to-blue-600 text-white font-black text-base rounded-2xl shadow-xl shadow-[#1a3884]/25 hover:shadow-2xl hover:shadow-[#1a3884]/30 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-3"
            >
              <Zap className="w-5 h-5" />
              Start Assessment
              <ChevronRight className="w-5 h-5 opacity-70" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── RESULT SCREEN ── */
  if (phase === 'result') {
    const { score, total, percentage, timeTaken, breakdown, alreadySubmitted } = result;
    const grade =
      percentage >= 80 ? { label: 'Excellent', color: 'text-emerald-600', bg: 'from-emerald-500 to-teal-500', ring: 'text-emerald-500' } :
      percentage >= 60 ? { label: 'Good', color: 'text-amber-600', bg: 'from-amber-500 to-orange-500', ring: 'text-amber-500' } :
      { label: 'Needs Work', color: 'text-red-600', bg: 'from-red-500 to-rose-500', ring: 'text-red-500' };

    const r = 58, circ = 2 * Math.PI * r;
    const offset = circ - (percentage / 100) * circ;

    const ringColors = { 'text-emerald-500': '#10b981', 'text-amber-500': '#f59e0b', 'text-red-500': '#ef4444' };
    const ringColor = ringColors[grade.ring] || '#1a3884';

    return (
      <div className="max-w-2xl mx-auto pt-8 pb-16 px-4 space-y-5">
        <button
          onClick={() => navigate('/student/micro-assessments')}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#1a3884] transition group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to Assessments
        </button>

        {/* Score Card */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-2xl overflow-hidden">
          <div className={`bg-gradient-to-br ${grade.bg} p-10 text-white text-center relative overflow-hidden`}>
            <div className="absolute inset-0 bg-black/10" />
            <div className="relative">
              <Trophy className="w-10 h-10 mx-auto mb-4 opacity-90" />
              <h1 className="text-2xl font-black mb-1">{alreadySubmitted ? 'Your Previous Result' : 'Assessment Complete!'}</h1>
              <p className="text-white/80 text-sm">{assessment?.assessmentName}</p>
            </div>
          </div>

          <div className="p-8 space-y-6">
            {/* Ring + grade */}
            <div className="flex flex-col items-center gap-3">
              <svg width="140" height="140" viewBox="0 0 140 140">
                <circle cx="70" cy="70" r={r} fill="none" stroke="#f1f5f9" strokeWidth="10" />
                <circle cx="70" cy="70" r={r} fill="none" stroke={ringColor} strokeWidth="10"
                  strokeDasharray={circ} strokeDashoffset={offset}
                  strokeLinecap="round" transform="rotate(-90 70 70)"
                  style={{ transition: 'stroke-dashoffset 1s ease' }}
                />
                <text x="70" y="65" textAnchor="middle" fontSize="26" fontWeight="800" fill="#1e293b">{percentage}%</text>
                <text x="70" y="85" textAnchor="middle" fontSize="11" fontWeight="600" fill="#94a3b8">Score</text>
              </svg>
              <div className={`text-2xl font-black ${grade.color}`}>{grade.label}</div>
              <p className="text-gray-500 text-sm">
                {score} out of {total} correct
                {timeTaken > 0 && ` · ${Math.floor(timeTaken / 60)}m ${timeTaken % 60}s`}
              </p>
            </div>

            {/* Score bar */}
            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
              <div className="flex justify-between text-xs font-semibold text-slate-500 mb-2">
                <span>Score Breakdown</span>
                <span>{score}/{total} Correct</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all duration-1000 ${percentage >= 80 ? 'bg-emerald-500' : percentage >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>

            {/* Breakdown */}
            {breakdown && breakdown.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Question Review</p>
                {breakdown.map((b, i) => (
                  <div key={i} className={`rounded-2xl p-4 border ${b.isCorrect ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}>
                    <div className="flex items-start gap-3">
                      <div className="shrink-0 mt-0.5">
                        {b.isCorrect
                          ? <CheckCircle className="w-4 h-4 text-emerald-500" />
                          : <XCircle className="w-4 h-4 text-red-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 mb-2">
                          <span className="text-gray-400 font-normal mr-1">Q{i + 1}.</span>
                          {b.questionText}
                        </p>
                        {b.explanation && (
                          <p className="text-xs text-slate-600 bg-white/80 rounded-lg px-2.5 py-2 mb-2 border border-slate-200/80">
                            <span className="font-bold text-slate-500">Explanation: </span>
                            {b.explanation}
                          </p>
                        )}
                        <div className="grid grid-cols-2 gap-1.5">
                          {b.options.map((opt) => {
                            const isSelected = b.selected === opt.value;
                            const isCorrect = b.correct === opt.value;
                            return (
                              <div key={opt.value}
                                className={`text-xs px-2.5 py-1.5 rounded-lg font-medium flex items-center gap-1.5 ${
                                  isCorrect ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' :
                                  isSelected && !isCorrect ? 'bg-red-100 text-red-700 border border-red-200' :
                                  'bg-white text-gray-500 border border-gray-100'
                                }`}
                              >
                                <span className="font-bold">{opt.value}.</span>
                                <span className="truncate">{opt.label}</span>
                                {isCorrect && <CheckCircle className="w-3 h-3 ml-auto shrink-0" />}
                                {isSelected && !isCorrect && <XCircle className="w-3 h-3 ml-auto shrink-0" />}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              {(assessment?.maxAttempts || 1) > (result.attemptNumber || 1) && (
                <button
                  onClick={() => {
                    const qs = assessment?.randomizeQuestions
                      ? shuffle(assessment.questions || [])
                      : (assessment.questions || []);
                    setQuestions(qs);
                    setAnswers({});
                    setResult(null);
                    setPhase('start');
                    setCurrentIdx(0);
                  }}
                  className="flex-1 py-3.5 bg-white border-2 border-[#1a3884] text-[#1a3884] font-bold rounded-2xl hover:bg-blue-50 transition-all"
                >
                  Retake (new shuffle)
                </button>
              )}
              <button
                onClick={() => navigate('/student/micro-assessments')}
                className="flex-1 py-3.5 bg-gradient-to-r from-[#1a3884] to-blue-600 text-white font-bold rounded-2xl shadow-lg hover:-translate-y-0.5 hover:shadow-xl transition-all"
              >
                Back to All Assessments
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── TAKING SCREEN ── */
  const currentQuestion = questions[currentIdx];
  const optionLabels = ['A', 'B', 'C', 'D'];

  return (
    <div className="max-w-3xl mx-auto pt-6 pb-16 px-4 space-y-5">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border border-gray-100 rounded-2xl shadow-lg px-5 py-3.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => { if (window.confirm('Leave assessment? Progress will be lost.')) navigate('/student/micro-assessments'); }}
            className="p-1.5 rounded-lg text-gray-400 hover:text-[#1a3884] hover:bg-blue-50 transition shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            <p className="font-bold text-gray-900 text-sm truncate">{assessment?.assessmentName}</p>
            <p className="text-xs text-gray-400">{answeredCount} of {questions.length} answered</p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="hidden sm:block w-28 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#1a3884] to-blue-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
          <span className="text-sm font-black text-[#1a3884]">{progress}%</span>
        </div>
      </div>

      {/* Question navigator pills */}
      <div className="flex flex-wrap gap-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        {questions.map((q, idx) => {
          const answered = !!answers[q._id];
          const isCurrent = idx === currentIdx;
          return (
            <button
              key={q._id}
              onClick={() => setCurrentIdx(idx)}
              className={`w-9 h-9 rounded-xl text-xs font-bold transition-all border ${
                isCurrent ? 'bg-[#1a3884] text-white border-[#1a3884] shadow-md shadow-[#1a3884]/20 scale-110' :
                answered ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                'bg-gray-50 text-gray-400 border-gray-200 hover:border-[#1a3884]/40 hover:text-[#1a3884]'
              }`}
            >
              {idx + 1}
            </button>
          );
        })}
      </div>

      {/* Current Question */}
      {currentQuestion && (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-xl overflow-hidden">
          {/* Question header */}
          <div className="bg-gradient-to-r from-[#00152E] to-[#1a3884] px-7 py-5 text-white">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold text-blue-300/80 uppercase tracking-widest">
                Question {currentIdx + 1} / {questions.length}
              </span>
              {currentQuestion.quotient && (
                <span className="text-xs bg-white/15 text-white px-2.5 py-1 rounded-full font-semibold">
                  {currentQuestion.quotient}
                </span>
              )}
            </div>
            <p className="text-lg font-bold leading-relaxed">{currentQuestion.questionText}</p>
          </div>

          {/* Options */}
          <div className="p-6 space-y-3">
            {(currentQuestion.options || []).map((opt, oi) => {
              const isSelected = answers[currentQuestion._id] === opt.value;
              return (
                <button
                  key={oi}
                  onClick={() => handleAnswer(currentQuestion._id, opt.value)}
                  className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all duration-200 group ${
                    isSelected
                      ? 'border-[#1a3884] bg-blue-50 shadow-md shadow-[#1a3884]/10'
                      : 'border-gray-100 bg-white hover:border-[#1a3884]/40 hover:bg-blue-50/40 hover:shadow-sm'
                  }`}
                >
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-black text-sm shrink-0 transition-all ${
                    isSelected ? 'bg-[#1a3884] text-white shadow-md' : 'bg-gray-100 text-gray-500 group-hover:bg-blue-100 group-hover:text-[#1a3884]'
                  }`}>
                    {optionLabels[oi] || opt.value}
                  </div>
                  <span className={`font-semibold text-sm flex-1 ${isSelected ? 'text-[#1a3884]' : 'text-gray-700 group-hover:text-gray-900'}`}>
                    {opt.label}
                  </span>
                  {isSelected && <CheckCircle className="w-5 h-5 text-[#1a3884] shrink-0" />}
                </button>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="px-6 pb-6 flex items-center justify-between gap-3">
            <button
              disabled={currentIdx === 0}
              onClick={() => setCurrentIdx((i) => i - 1)}
              className="flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-semibold hover:border-[#1a3884]/40 hover:text-[#1a3884] disabled:opacity-30 disabled:cursor-not-allowed transition"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>

            {currentIdx < questions.length - 1 ? (
              <button
                onClick={() => setCurrentIdx((i) => i + 1)}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#1a3884] text-white rounded-xl text-sm font-bold hover:bg-[#00152E] transition shadow-md"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting || answeredCount < questions.length}
                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl text-sm font-black hover:shadow-lg hover:shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {submitting ? (
                  <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Submitting…</>
                ) : (
                  <><Send className="w-4 h-4" />Submit Assessment</>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Submit bar when on last + all answered */}
      {answeredCount === questions.length && currentIdx < questions.length - 1 && (
        <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 flex items-center justify-between gap-4 shadow-sm">
          <div className="flex items-center gap-2.5">
            <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />
            <p className="text-sm font-semibold text-emerald-700">All {questions.length} questions answered!</p>
          </div>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-black hover:bg-emerald-700 transition shadow-md disabled:opacity-60"
          >
            <Send className="w-4 h-4" />
            {submitting ? 'Submitting…' : 'Submit Now'}
          </button>
        </div>
      )}
    </div>
  );
};

export default MicroAssessmentPlayer;
